import React, { useEffect, useMemo, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { apiService } from '../services/apiService';
import { useToast } from '../components/Toast';
import { useAuth } from '../services/authContext';

const emptyForm = { name: '', teacherId: '', description: '' };

const Classes = () => {
  const { user }                          = useAuth();
  const { toast }                         = useToast();
  const isAdmin                           = user?.role === 'admin';
  const [classes, setClasses]             = useState([]);
  const [teachers, setTeachers]           = useState([]);
  const [modules, setModules]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState('');
  const [search, setSearch]               = useState('');
  const [isModalOpen, setIsModalOpen]     = useState(false);
  const [current, setCurrent]             = useState(null);
  const [form, setForm]                   = useState({ name: '', teacherId: '', description: '', programme: '' });
  // Module management
  const [modulesPanel, setModulesPanel]   = useState(null); // { classId, className, subjects }
  const [addModuleId, setAddModuleId]     = useState('');
  const [addTeacherId, setAddTeacherId]   = useState('');
  const [modulesSaving, setModulesSaving] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true); setError('');
      const [classData, teacherData, subjectData] = await Promise.all([
        apiService.getAll('classes'),
        apiService.getAll('teachers'),
        apiService.getAll('modules'),
      ]);
      setClasses(classData    || []);
      setTeachers(teacherData || []);
      setModules(subjectData || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load classes.');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const filteredClasses = useMemo(() =>
    classes.filter(item =>
      [item.name, item.description, item.teacher?.name].join(' ').toLowerCase().includes(search.toLowerCase())
    ), [classes, search]);

  const openAdd = () => { setCurrent(null); setForm(emptyForm); setIsModalOpen(true); };

  const openEdit = item => {
    setCurrent(item);
    setForm({ name: item.name || '', teacherId: item.teacher?._id || '', description: item.description || '', programme: item.programme || '' });
    setIsModalOpen(true);
  };

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      setSaving(true); setError('');
      const payload = { ...form, teacherId: form.teacherId || null };
      if (current) {
        await apiService.update('classes', current._id, payload);
        toast('Class updated successfully.', 'success');
      } else {
        await apiService.create('classes', payload);
        toast('Class created successfully.', 'success');
      }
      setIsModalOpen(false); setForm(emptyForm);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save class.');
      toast(err.response?.data?.message || 'Failed to save programme.', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this programme? This will also remove all linked students and subjects.')) return;
    try {
      await apiService.delete('classes', id);
      toast('Programme deleted.', 'success');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete.');
      toast(err.response?.data?.message || 'Failed to delete.', 'error');
    }
  };

  // Open modules panel for a class
  const openModules = async item => {
    try {
      const subs = await apiService.get(`modules/class/${item._id}`);
      setModulesPanel({ classId: item._id, className: item.name, modules: Array.isArray(subs) ? subs : [] });
      setAddModuleId(''); setAddTeacherId('');
    } catch { setModulesPanel({ classId: item._id, className: item.name, modules: [] }); }
  };

  const refreshModules = async () => {
    if (!modulesPanel) return;
    const subs = await apiService.get(`modules/class/${modulesPanel.classId}`);
    setModulesPanel(p => ({ ...p, modules: Array.isArray(subs) ? subs : [] }));
  };

  const handleAddModule = async () => {
    if (!addModuleId) { toast('Select a module to add.', 'warning'); return; }
    try {
        setModulesSaving(true);
        await apiService.post('modules/class', {
          classId:   modulesPanel.classId,
          moduleId: addModuleId,
          teacherId: addTeacherId || null,
        });
      toast('Module added to programme.', 'success');
      setAddModuleId(''); setAddTeacherId('');
      await refreshModules();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to add module.', 'error');
    } finally { setModulesSaving(false); }
  };

  const handleRemoveModule = async moduleId => {
    if (!window.confirm('Remove this module from the programme?')) return;
    try {
      await apiService.raw.delete(`modules/class/${modulesPanel.classId}/${moduleId}`);
      toast('Module removed.', 'success');
      await refreshModules();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to remove module.', 'error');
    }
  };

  // Subjects not yet assigned to this class
  const unassignedModules = useMemo(() => {
    if (!modulesPanel) return [];
    const assigned = new Set(modulesPanel.modules.map(s => s._id));
    return modules.filter(s => !assigned.has(s._id));
  }, [modulesPanel, modules]);

  if (loading) return <LoadingSpinner message="Loading classes..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{isAdmin ? 'Programmes & Classes' : 'My Assigned Classes'}</h1>
        {isAdmin && <button className="btn btn-primary" onClick={openAdd}>+ Add Class</button>}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="search-container">
        <input type="text" placeholder="Search classes..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="table-responsive">
        <table className="data-table">
          <thead>
            {isAdmin ? (
              <tr><th>Name</th><th>Programme</th><th>Lecturer</th><th>Description</th><th>Actions</th></tr>
            ) : (
              <tr><th>Class Name</th><th>Semester</th><th>Student Count</th><th>Description</th><th>Actions</th></tr>
            )}
          </thead>
          <tbody>
            {filteredClasses.length === 0 ? (
              <tr><td colSpan={isAdmin ? 5 : 5} className="no-data-message">No classes found.</td></tr>
            ) : filteredClasses.map(item => (
              <tr key={item._id}>
                <td><strong>{item.name}</strong></td>
                {isAdmin ? (
                  <>
                    <td>{item.programme || '—'}</td>
                    <td>{item.teacher?.name || 'Not assigned'}</td>
                  </>
                ) : (
                  <>
                    <td>{item.semester || '—'}</td>
                    <td>{item.studentCount || 0} students</td>
                  </>
                )}
                <td>{item.description || '—'}</td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-icon" style={{ color: '#1976D2' }} onClick={() => openModules(item)}>View Modules</button>
                    <button className="btn-icon" style={{ color: '#388E3C' }} onClick={() => window.location.href=`/attendance?classId=${item._id}`}>Attendance</button>
                    {isAdmin && <button className="btn-icon" onClick={() => openEdit(item)}>Edit</button>}
                    {isAdmin && <button className="btn-icon" style={{ color: '#c62828' }} onClick={() => handleDelete(item._id)}>Delete</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Class Modal */}
      {isAdmin && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={current ? 'Edit Class' : 'Add Class'}>
          <form onSubmit={handleSubmit} className="form">
            <div className="form-group">
              <label>Academic Programme</label>
              <select name="programme" value={form.programme} onChange={handleChange}>
                <option value="">Select Programme</option>
                <option value="Film Studies">Film Studies</option>
                <option value="Information Technology">Information Technology</option>
                <option value="Graphic Design">Graphic Design</option>
              </select>
            </div>
            <div className="form-group">
              <label>Class Name</label>
              <input name="name" value={form.name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Class Lecturer / Mentor</label>
              <select name="teacherId" value={form.teacherId} onChange={handleChange}>
                <option value="">Select Lecturer</option>
                {teachers.map(t => (
                  <option key={t._id} value={t._id}>{t.firstName} {t.lastName}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Description / Location</label>
              <textarea name="description" value={form.description} onChange={handleChange} rows={3} />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : current ? 'Update' : 'Create Class'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modules Panel Modal */}
      <Modal isOpen={!!modulesPanel} onClose={() => setModulesPanel(null)} title={`Modules — ${modulesPanel?.className}`}>
        {modulesPanel && (
          <div>
            {/* Assigned modules list */}
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Assigned Modules ({modulesPanel.modules.length})</h3>
            {modulesPanel.modules.length === 0 ? (
              <div className="no-data-message" style={{ marginBottom: '1.5rem' }}>No modules assigned yet.</div>
            ) : (
              <div className="table-responsive" style={{ marginBottom: '1.5rem' }}>
                <table className="data-table">
                  <thead><tr><th>Code</th><th>Module</th><th>Lecturer</th>{isAdmin && <th>Action</th>}</tr></thead>
                  <tbody>
                    {modulesPanel.modules.map(s => (
                      <tr key={s._id}>
                        <td><strong>{s.code}</strong></td>
                        <td>{s.name}</td>
                        <td>{s.teacherName || 'Not assigned'}</td>
                        {isAdmin && (
                          <td>
                            <button className="btn-icon" style={{ color: '#c62828' }} onClick={() => handleRemoveModule(s._id)}>Remove</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Add module section — admin only */}
            {isAdmin && (
              <>
                <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem' }}>Add Module</h3>
                {unassignedModules.length === 0 ? (
                  <div style={{ color: 'var(--text-light)', fontSize: '0.85rem', marginBottom: '1rem' }}>All available modules are already assigned.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Module</label>
                        <select value={addModuleId} onChange={e => setAddModuleId(e.target.value)}>
                          <option value="">Select Module</option>
                          {unassignedModules.map(s => (
                            <option key={s._id} value={s._id}>{s.code} — {s.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Assign Lecturer</label>
                        <select value={addTeacherId} onChange={e => setAddTeacherId(e.target.value)}>
                          <option value="">Select Lecturer</option>
                          {teachers.map(t => (
                            <option key={t._id} value={t._id}>{t.firstName} {t.lastName}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <button className="btn btn-primary" onClick={handleAddModule} disabled={modulesSaving} style={{ alignSelf: 'flex-start' }}>
                      {modulesSaving ? 'Adding...' : '+ Add Module'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Classes;
